// Initial state
const initialState = {
  practiceTests: [],
  practiceAssessments: [],
  practiceAssessmentResult: null,
  currentPracticeTest: null,
  batchQuestions: null,
  submissions: {},
  testCases: [],
  recordings: {},
  activeQuestion: null,
  activeSubmission: null,
  loading: false,
  error: null,
};

// Reducer
const practiceTestReducer = (state = initialState, action) => {
  switch (action.type) {
    case 'GET_PRACTICE_TESTS_SUCCESS':
      return {
        ...state,
        practiceTests: action.payload,
        loading: false,
        error: null,
      };
    case 'GET_PRACTICE_ASSESSMENTS_SUCCESS':
      return {
        ...state,
        practiceAssessments: action.payload,
        loading: false,
        error: null,
      };
    case 'GET_PRACTICE_ASSESSMENT_RESULT_SUCCESS':
      // Transform the API response to match the UI structure
      const transformedData = {
        ...action.payload,
        questions: {},
      };

      // Add MCQs to questions object
      if (action.payload.mcqs) {
        action.payload.mcqs.forEach(mcq => {
          transformedData.questions[mcq.id] = {
            ...mcq,
            type: 'mcq',
          };
        });
      }

      // Add problems (coding questions) to questions object
      if (action.payload.problems) {
        action.payload.problems.forEach(problem => {
          transformedData.questions[problem.id] = {
            ...problem,
            type: 'coding',
          };
        });
      }

      // Add sections if not present
      if (!transformedData.sections && (action.payload.mcqs || action.payload.problems)) {
        transformedData.sections = [
          {
            id: '1',
            name: 'All Questions',
            questions: Object.keys(transformedData.questions),
            mcqs: action.payload.mcqs?.map(mcq => mcq.id) || [],
            problems: action.payload.problems?.map(problem => problem.id) || [],
          },
        ];
      } else {
        // Sort sections by their order field
        transformedData.sections = [...transformedData.sections].sort((a, b) => a.order - b.order);
      }

      // Set initial active question from first section's first question
      let initialActiveQuestion = null;
      if (transformedData.sections && transformedData.sections.length > 0) {
        const firstSection = transformedData.sections[0];
        const firstSectionQuestionIds = [
          ...(firstSection.problems || []).map(p => p.id),
          ...(firstSection.mcqs || []).map(m => m.id),
        ];

        if (firstSectionQuestionIds.length > 0) {
          initialActiveQuestion = transformedData.questions[firstSectionQuestionIds[0]] || null;
        }
      } else if (Object.keys(transformedData.questions || {}).length > 0) {
        // Fallback to first question if no sections
        initialActiveQuestion =
          transformedData.questions[Object.keys(transformedData.questions)[0]] || null;
      }

      return {
        ...state,
        practiceAssessmentResult: transformedData,
        activeQuestion: initialActiveQuestion,
        loading: false,
        error: null,
      };
    case 'START_PRACTICE_TEST_SUCCESS':
      return {
        ...state,
        currentPracticeTest: action.payload,
        loading: false,
        error: null,
      };
    case 'GET_BATCH_QUESTIONS_SUCCESS':
      // Merge batch questions with existing questions
      const updatedQuestions = { ...state.practiceAssessmentResult?.questions };
      action.payload.forEach(question => {
        // The questions are already parsed into a simple format (admin portal style)
        updatedQuestions[question.id] = {
          ...updatedQuestions[question.id],
          ...question,
        };
      });

      // Update practiceAssessmentResult with merged questions
      const updatedPracticeAssessmentResult = {
        ...state.practiceAssessmentResult,
        questions: updatedQuestions,
      };

      // Update activeQuestion if it's one of the merged questions
      let updatedActiveQuestion = state.activeQuestion;
      if (state.activeQuestion && updatedQuestions[state.activeQuestion.id]) {
        updatedActiveQuestion = updatedQuestions[state.activeQuestion.id];
      }

      return {
        ...state,
        batchQuestions: action.payload,
        practiceAssessmentResult: updatedPracticeAssessmentResult,
        activeQuestion: updatedActiveQuestion,
        loading: false,
        error: null,
      };
    case 'GET_PRACTICE_SUBMISSIONS_SUCCESS':
      const { question_id, data } = action.payload;
      // Find the best code submission from the code submissions array
      const bestSubmission = data.code_submissions.find(
        sub => sub.id === data.best_code_submission,
      );

      return {
        ...state,
        submissions: {
          ...state.submissions,
          [question_id]: data,
        },
        // Set initial active submission if none exists
        activeSubmission: state.activeSubmission || bestSubmission || null,
        loading: false,
        error: null,
      };
    case 'GET_PRACTICE_TEST_CASES_SUCCESS':
      return {
        ...state,
        testCases: action.payload,
        loading: false,
        error: null,
      };
    case 'GET_PRACTICE_RECORDINGS_SUCCESS':
      return {
        ...state,
        recordings: {
          ...state.recordings,
          [action.payload.assessment_id]: action.payload.recordings,
        },
        loading: false,
        error: null,
      };
    case 'SET_ACTIVE_QUESTION':
      // Find the question object from the questions map using the active_question id
      const { active_question: questionId } = action.payload;
      const question = state.practiceAssessmentResult?.questions?.[questionId];

      return {
        ...state,
        activeQuestion: question || null,
        activeSubmission: null, // Reset active submission when question changes
      };
    case 'SET_ACTIVE_SUBMISSION':
      // Find the submission from the submissions state
      const submission = state.submissions[action.payload.question_id]?.code_submissions?.find(
        sub => sub.id === action.payload.submission_id,
      );
      return {
        ...state,
        activeSubmission: submission || null,
      };
    case 'AXIOS_CALL_REQUEST':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'AXIOS_CALL_SUCCESS':
      return {
        ...state,
        loading: false,
      };
    case 'AXIOS_CALL_ERROR':
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    default:
      return state;
  }
};

export default practiceTestReducer;
